import React, { useEffect, useState } from 'react'
import {
	fetchAccountUsers,
	uploadUserManualFile,
	downloadUserManualFile,
} from './account.asyncActions'
import { useDispatch, useSelector } from 'react-redux'
import AccountModal from '../AccountModal'
import Table from '../../widgets/Table'
import {
	selectAccountUsers,
	selectAccountModal,
	selectEmail,
	selectuserManualFile,
} from './account.selector'
import { ACCOUNT_MODALS, STATUS, USER_ROLES } from './account.constants'
import { actions } from './account.slice'
import Icons from '../../assets/icons'
import { Button } from '@/components/ui/button'
import { Alert } from '@/components/ui/alert'
import SortingCustomIcon from '../../widgets/SortingCustomIcon'
import BusyIndicator, { REGIONS } from '../../widgets/busyIndicator'
import { selectUser } from '../Profile/profile.selectors'
import Dropzone from 'react-dropzone'
import { isEmpty } from 'lodash'
import PageLayout from '../../components/ui/page-layout'

const timeOutForAlertPrompt = 3000
const emptyString = ''

const { setAccountModal, setIsAddUserModal, setSelectedRow } = actions

const Account = () => {
	const selectedAccountModal = useSelector(selectAccountModal)
	const email = useSelector(selectEmail)
	const dispatch = useDispatch()
	const user = useSelector(selectUser)
	const userManualFile = useSelector(selectuserManualFile)

	const [errorMessage, setErrorMessage] = useState(emptyString)
	const [isUserManualUploaded, setIsUserManualUploaded] = useState(false)

	const { uri, fileName } = userManualFile

	useEffect(() => {
		if (isEmpty(userManualFile)) dispatch(downloadUserManualFile())
	}, [dispatch, userManualFile])

	useEffect(() => {
		errorMessage &&
			setTimeout(() => {
				setErrorMessage(emptyString)
			}, timeOutForAlertPrompt)
	}, [dispatch, errorMessage])

	const onDropFile = (acceptedFiles) => {
		const formData = new FormData()
		acceptedFiles &&
			acceptedFiles.forEach((each) => {
				formData.append('userIds', user.id)
				formData.append('files', each)
			})
		dispatch(uploadUserManualFile(formData)).then(() => {
			setIsUserManualUploaded(true)
			dispatch(downloadUserManualFile())
			setTimeout(() => {
				setIsUserManualUploaded(false)
			}, timeOutForAlertPrompt)
		})
	}

	const userOptions = (id, row) => {
		const isInvite = !row.lastSignIn
		return (
			<div className='flex justify-end whitespace-nowrap items-center'>
				<div
					className='pointer f-5 font-semi-bold flex items-center'
					style={{ width: '160px' }}
					onClick={() => {
						dispatch(setSelectedRow(row))
						dispatch(setAccountModal(ACCOUNT_MODALS.RESET_MODAL))
					}}
				>
					<img src={Icons.Reset} width='15' alt='arrow' className='mr-2 inline-block' />
					{isInvite ? 'Resend Invitation' : 'Reset Password'}
				</div>
				<div
					className='f-5 pointer font-semi-bold flex items-center ml-20'
					onClick={() => dispatch(setAccountModal(ACCOUNT_MODALS.DELETE_MODAL))}
				>
					<img src={Icons.Delete} width='15' alt='arrow-down-icon' className='mr-2 inline-block' />
					Delete User
				</div>
				<div
					className='f-5 pointer font-semi-bold flex items-center ml-20'
					onClick={() => {
						dispatch(setIsAddUserModal(false))
						dispatch(setAccountModal(ACCOUNT_MODALS.EDIT_MODAL))
					}}
				>
					<img src={Icons.Edit} width='15' alt='arrow-down-icon' className='mr-2 inline-block' />
					Edit User
				</div>
			</div>
		)
	}

	const [notifyToggle, setNotifyToggle] = useState({ show: false })

	useEffect(() => {
		dispatch(fetchAccountUsers())
	}, [dispatch])

	useEffect(() => {
		if (notifyToggle.show) {
			setTimeout(() => {
				setNotifyToggle({ show: false })
			}, 3000)
		}
	}, [dispatch, notifyToggle.show])

	const accountUsers = useSelector(selectAccountUsers)

	const roleFormatter = (cell, row) => USER_ROLES[row.userRole]

	const statusFormatter = (cell, row) => (
		<div className='text-nowrap'>
			{row.lastSignIn ? STATUS[row.status] : STATUS[2]}
		</div>
	)

	const columns = [
		{
			dataField: 'name',
			text: 'User',
			headerStyle: { width: '180px' },
			align: 'left',
			sort: true,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'email',
			text: 'Email',
			sort: true,
			sortCaret: SortingCustomIcon,
			headerStyle: { width: '120px' },
			align: 'left',
		},
		{
			dataField: 'userRole',
			text: 'User Role',
			headerStyle: { width: '150px' },
			align: 'left',
			sort: true,
			formatter: roleFormatter,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'totalAssignedAccounts',
			text: 'Assignment Count',
			headerStyle: { width: '180px' },
			align: 'left',
			sort: true,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: 'status',
			text: 'Status',
			headerStyle: { width: '270px' },
			align: 'left',
			sort: true,
			formatter: statusFormatter,
			sortCaret: SortingCustomIcon,
		},
		{
			dataField: '',
			text: '',
			formatter: userOptions,
		},
	]

	const rowEvents = {
		onClick: (e, row) => {
			dispatch(setSelectedRow(row))
		},
	}

	return (
		<div className='ml-15 mr-15 mt-30'>
			{!isEmpty(errorMessage) && (
				<Alert variant='destructive' className='mb-30'>
					{errorMessage}
				</Alert>
			)}
			{selectedAccountModal &&
				AccountModal(selectedAccountModal, setNotifyToggle)}
			{notifyToggle.show && (
				<Alert className='mb-30 border border-green-300 text-green-700 bg-green-50'>
					{notifyToggle.message || `An invite was successfully sent to ${email}`}
				</Alert>
			)}
			{isUserManualUploaded && (
				<Alert className='mb-30 border border-green-300 text-green-700 bg-green-50'>
					Cloud 4473 User Manual has been uploaded.
				</Alert>
			)}
			<PageLayout title='Files'>
			<div className='mb-30 m-auto rounded border bg-white '>
				<div className='flex flex-col lg:flex-row items-center gap-3 px-3 py-3'>
					<div className='w-full lg:w-1/3 text-left'>
						Cloud 4473 User Manual
					</div>
					<div className='w-full lg:w-1/3 text-left font-weight-normal'>
						<BusyIndicator region={REGIONS.DOWNLOAD_USER_MANUAL}>
							{fileName || 'No User Manual Found.'}
						</BusyIndicator>
					</div>
					<div className='w-full lg:w-1/3 flex items-center justify-end gap-2 mt-4 lg:mt-0'>
						<Button
							variant='outline'
							onClick={() => window.open(uri)}
							disabled={!uri}
						>
							<BusyIndicator region={REGIONS.DOWNLOAD_USER_MANUAL}>
								<span className='inline-flex items-center'>
									<img src={Icons.Download} width='20' alt='arrow' className='rounded-circle ml-1 mr-1 inline-block' />
									Download File
								</span>
							</BusyIndicator>
						</Button>
						<div>
							<Dropzone
								onDrop={(acceptedFiles) => onDropFile(acceptedFiles)}
								accept={['.pdf']}
							>
								{({ getRootProps, getInputProps }) => (
									<div id='cardForDropZone' {...getRootProps()}>
										<Button variant='outline' className='pr-0'>
											<BusyIndicator region={REGIONS.UPLOAD_USER_MANUAL}>
												<span className='inline-flex items-center'>
													<img src={Icons.Upload} width='20' alt='arrow' className='rounded-circle ml-1 mr-1 inline-block' />
													Replace File
												</span>
											</BusyIndicator>
										</Button>
										<input {...getInputProps()} />
									</div>
								)}
							</Dropzone>
						</div>
					</div>
				</div>
			</div>
			</PageLayout>
			
			
			<PageLayout title='Account Users' action={					<Button
						onClick={() => {
							dispatch(setIsAddUserModal(true))
							dispatch(setSelectedRow({}))
							dispatch(setAccountModal(ACCOUNT_MODALS.EDIT_MODAL))
						}}
					>
						Add User
					</Button>}>
				<Table
					keyField='id'
					columns={columns}
					data={accountUsers}
					scrollContainerClassName='max-h-[calc(100vh-500px)] overflow-y-auto'
					rowEvents={rowEvents}
					region={REGIONS.FETCH_ACCOUNTS}
				/>
			<div className='spacer'></div>
			</PageLayout>
		</div>
	

		
	)
}
export default Account
